import { PureComponent } from 'react';
import { NextRouter } from 'next/dist/client/router';

import withBreakpoints, {
  InjectedProps as WithBreakpointsProps,
} from 'lib/withBreakpoints';

import { Button, Link, Img, LinkOrButton } from 'components/base';
import {
  Base,
  AnchorLabel,
  SideNavCloseIcon,
  SideNavArrowSmall,
  SideNavArrowLarge,
} from 'styled';
import {
  Container,
  TopBar,
  HomeButton,
  SymptomCheckerButton,
  TopNavMenuButtonContainer,
  CloseSideNavButtonContainer,
  CloseSideNavButton,
  OpenSideNavButtonContainer,
  OpenSideNavButton,
  Nav,
  MainMenuContainer,
  MainMenuButton,
  MainMenuSubNav,
} from './style';

import { RouteMap } from 'constants/Routes';
import Language from 'constants/Language';

import { NavList, NavListItem } from 'types';

interface PassedProps {
  router: NextRouter;
  sideNavigation: NavList;
  sideNavigationByOrder: string[];
  businessOnboardingPopUpIsActive: boolean;
  notificationBarIsActive: boolean | null;
  notificationBarHeight: number;
  renderMinimalNav: boolean;
}

export type Props = PassedProps & WithBreakpointsProps;

interface SubListHeights {
  [slug: string]: {
    desktop: number;
    mobile: number;
  };
}

export interface State {
  activeList: string | null;
  menuIsActive: boolean;
  sublistHeights: SubListHeights;
  hasMounted: boolean;
}

/*
 * The SideNav has a "minimal" version in which it only has the logo (home button),
 * no other navigational elements, and no symptom checker button.
 *
 * On desktop view, it does not render the side nav, but instead, continues to show
 * the top bar similar to tablet view.
 */

class SideNav extends PureComponent<Props, State> {
  private sublistTopMargins = 1.5;

  constructor(props: Props) {
    super(props);

    const sublistHeights = Object.values(props.sideNavigation).reduce(
      (sanitizedSublistHeight: SubListHeights, navList: NavListItem) => {
        const sublist = navList.children;

        if (!!sublist.length) {
          sanitizedSublistHeight[navList.slug] = {
            desktop: sublist.length * (17 + this.sublistTopMargins * 16),
            mobile: sublist.length * (28 + this.sublistTopMargins * 16),
          };
        }

        return sanitizedSublistHeight;
      },
      {}
    );

    this.state = {
      activeList: null,
      menuIsActive: false,
      sublistHeights,
      hasMounted: false,
    };
  }

  componentDidMount() {
    this.props.router.events.on('routeChangeStart', this.handleRouteChange);

    // Force side nav to re-render to properly set tabindex based on breakpoints
    this.setState({ hasMounted: true });
  }

  componentDidUpdate(prevProps: Props) {
    if (
      this.breakpointIsMdUp(prevProps.currentBreakpoint) !==
      this.breakpointIsMdUp(this.props.currentBreakpoint)
    ) {
      this.handleCloseMenu();
    }

    if (
      !prevProps.businessOnboardingPopUpIsActive &&
      this.props.businessOnboardingPopUpIsActive
    ) {
      this.handleCloseMenu();
    }
  }

  componentWillUnmount() {
    this.props.router.events.off('routeChangeStart', this.handleRouteChange);
  }

  handleRouteChange = () => {
    this.handleCloseMenu();
  };

  breakpointIsMdUp = (breakpoint: string) => {
    return ['LARGE', 'EXTRA_LARGE', 'EXTRA_EXTRA_LARGE'].includes(breakpoint);
  };

  handleCloseMenu = () => {
    if (this.breakpointIsMdUp(this.props.currentBreakpoint)) {
      this.setState({ menuIsActive: false });
    } else {
      this.setState({ menuIsActive: false, activeList: null });
    }
  };

  handleListClick = (list: string) => {
    this.setState((state) => {
      const activeList = state.activeList;

      if (activeList === list) {
        return {
          activeList: null,
        };
      }
      return { activeList: list };
    });
  };

  handleOpenMenu = () => {
    this.setState({ menuIsActive: true });
  };

  render() {
    const {
      currentBreakpoint,
      notificationBarIsActive,
      notificationBarHeight,
      sideNavigation,
      sideNavigationByOrder,
      renderMinimalNav,
      router: { asPath, pathname },
    } = this.props;
    const { activeList, menuIsActive, sublistHeights, hasMounted } = this.state;
    const breakpointIsMdUp = this.breakpointIsMdUp(currentBreakpoint);

    if (!hasMounted) {
      return null;
    }

    return (
      <Container renderMinimalNav={renderMinimalNav}>
        <TopBar
          display="flex"
          flexDirection={
            renderMinimalNav ? 'row' : ['row', 'row', 'row', 'column']
          }
          justifyContent={
            renderMinimalNav
              ? 'space-between'
              : ['space-between', 'space-between', 'space-between', 'center']
          }
          alignItems="center"
          mx="auto"
          renderMinimalNav={renderMinimalNav}
        >
          <HomeButton
            as={Link}
            display="flex"
            alignItems="center"
            justifyContent="center"
            ariaLabel={Language.t('Global.homeButtonAriaLabel')}
            to={RouteMap.HOME.path}
          >
            <Img
              alt={Language.t('Global.logoAlt')}
              src="/images/buoy-logo.svg"
            />
          </HomeButton>
          {!renderMinimalNav && !breakpointIsMdUp && (
            <SymptomCheckerButton
              as={LinkOrButton}
              ariaLabel={Language.t('Global.symptomCheckerAriaLabel')}
              label={Language.t('Global.symptomCheckerLabel')}
              to={RouteMap.SYMPTOM_CHECKER.path}
              variant="symptom-checker-white"
            />
          )}
          {!renderMinimalNav && (
            <TopNavMenuButtonContainer
              position="relative"
              display={['flex', 'flex', 'flex', 'none']}
              alignItems="center"
              justifyContent="center"
            >
              <CloseSideNavButtonContainer menuIsActive={menuIsActive}>
                <CloseSideNavButton
                  as={Button}
                  ariaLabel={Language.t('SideNav.closeMenu')}
                  onClick={this.handleCloseMenu}
                  tabIndex={breakpointIsMdUp || menuIsActive ? 0 : -1}
                >
                  <SideNavCloseIcon />
                </CloseSideNavButton>
              </CloseSideNavButtonContainer>
              <OpenSideNavButtonContainer menuIsActive={menuIsActive}>
                <OpenSideNavButton
                  as={Button}
                  ariaLabel={Language.t('SideNav.openMenu')}
                  disabled={menuIsActive}
                  onClick={this.handleOpenMenu}
                  tabIndex={breakpointIsMdUp || menuIsActive ? -1 : 0}
                >
                  <Img
                    alt={Language.t('SideNav.openMenu')}
                    src="/images/menu-dot-icon-blue.svg"
                  />
                </OpenSideNavButton>
              </OpenSideNavButtonContainer>
            </TopNavMenuButtonContainer>
          )}
        </TopBar>
        {!renderMinimalNav && (
          <Nav
            currentBreakpoint={currentBreakpoint}
            notificationBarIsActive={notificationBarIsActive}
            notificationBarHeight={notificationBarHeight}
            menuIsActive={menuIsActive}
          >
            <MainMenuContainer
              width="100%"
              overflowY="scroll"
              pt={[0.75, 0.75, 0.75, 1.25]}
              pb={[1.5, 1.5, 1.5, 2.25]}
              currentBreakpoint={currentBreakpoint}
              notificationBarIsActive={notificationBarIsActive}
              notificationBarHeight={notificationBarHeight}
            >
              {sideNavigationByOrder.map((key: string) => {
                const list = sideNavigation[key];

                if (!list) {
                  return null;
                }

                const status =
                  activeList === list.label
                    ? Language.t('Global.close')
                    : Language.t('Global.open');

                if (!list.children.length) {
                  return (
                    <li key={list.label}>
                      <MainMenuButton
                        as={LinkOrButton}
                        px={1.5}
                        py={[0.75, 0.75, 0.75, 1.25]}
                        ariaLabel={`${list.label}.`}
                        isCurrentPath={asPath === list.slug}
                        label={list.label}
                        to={list.slug}
                        variant="side-nav-menu"
                        tabIndex={breakpointIsMdUp || menuIsActive ? 0 : -1}
                      />
                    </li>
                  );
                } else {
                  const sectionIsExpanded = activeList === list.label;

                  return (
                    <li key={list.label}>
                      <MainMenuButton
                        as={Button}
                        px={1.5}
                        py={[0.75, 0.75, 0.75, 1.25]}
                        ariaLabel={Language.t('SideNav.dropdownAriaLabel', {
                          status,
                          title: list.label,
                        })}
                        onClick={() => this.handleListClick(list.label)}
                        variant="side-nav-menu"
                        tabIndex={breakpointIsMdUp || menuIsActive ? 0 : -1}
                      >
                        <AnchorLabel>{list.label}</AnchorLabel>
                        <Base
                          as={
                            breakpointIsMdUp
                              ? SideNavArrowSmall
                              : SideNavArrowLarge
                          }
                          isReverse={sectionIsExpanded}
                        />
                      </MainMenuButton>
                      <MainMenuSubNav
                        sectionIsExpanded={sectionIsExpanded}
                        sublistHeights={sublistHeights}
                        breakpointIsMdUp={breakpointIsMdUp}
                        sectionSlug={list.slug}
                      >
                        {list.children.map((sublist) => (
                          <li key={`${list.label}-${sublist.label}`}>
                            <MainMenuButton
                              as={LinkOrButton}
                              pl={[3, 3, 3, 2.25]}
                              pr={1.5}
                              py={0.75}
                              ariaLabel={`${sublist.label}.`}
                              tabIndex={activeList !== list.label ? -1 : 0}
                              isCurrentPath={asPath === sublist.slug}
                              label={sublist.label}
                              to={sublist.slug}
                              variant="side-nav-menu"
                            />
                          </li>
                        ))}
                      </MainMenuSubNav>
                    </li>
                  );
                }
              })}
            </MainMenuContainer>
            {/* <AccountSection
            display="flex"
            flexDirection="column"
            alignItems="start"
            justifyContent="center"
            mx="auto"
          >
            <Button
              ariaLabel={`${Language.t('SideNav.account')}.`}
              label={Language.t('SideNav.account')}
              to={RouteMap.SIGN_UP.path}
              variant="side-nav-menu"
              tabIndex={breakpointIsMdUp || menuIsActive ? 0 : -1}
            />
          </AccountSection> */}
          </Nav>
        )}
      </Container>
    );
  }
}

export default withBreakpoints<Props>(SideNav);
